import React, { useContext, useState, useEffect, useRef } from 'react';
import { EventDetailsContext } from '../../context/event_details.context';
import classNames from 'classnames';
import Modal from 'react-bootstrap/Modal';
import { updateUserData } from '../../../services/current_user/current_user.service';
import AutocompleteBootstrap5 from '../../../components/react/autocompletes/autocomplete_bootstrap_5';
import {
  autocompletePlacesPath,
  autocompleteCompaniesPath,
} from '../../../utilities/api_relative_paths';
import { GENERIC_ERROR_MESSAGE } from '../../../components/react/alert_popup/constants';
import { AutocompleteEntry } from '../../types/event_details.types';
import pickBy from 'lodash/pickBy';
const specialCharactersRegex = /[!@#$%^&*(),?":{}|=+<>]/;
const QUESTION_MAX_SIZE = 500;

interface RequiredValidations {
  firstName: boolean;
  lastName: boolean;
  jobTitle: boolean;
  location: boolean;
  company: boolean;
}

interface SpecialCharacterValidations {
  firstName: boolean;
  lastName: boolean;
}

interface MaxCharValidations {
  question: boolean;
}

interface TooManyDigitsValidations {
  firstName: boolean;
  lastName: boolean;
}

interface ValidationErrors {
  required: RequiredValidations;
  specialCharacter: SpecialCharacterValidations;
  maxChar: MaxCharValidations;
  tooManyDigits: TooManyDigitsValidations;
}

const ConfirmInfo: React.FC = () => {
  const companyInputValue = useRef<string>('');
  const [isFormTouched, setIsFormTouched] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasFormBeenValidated, setHasFormBeenValidated] = useState<boolean>(false);
  const {
    isSignedIn,
    event,
    showConfirmInfo,
    setShowConfirmInfo,
    userDetails,
    triggerRegistration,
    questionForSpeaker,
    setQuestionForSpeaker,
    setAlertData,
  } = useContext(EventDetailsContext);
  const { id, firstName, lastName, jobTitle, location, company } = userDetails;
  const [firstNameModel, setFirstNameModel] = useState<string>(firstName || '');
  const [lastNameModel, setLastNameModel] = useState<string>(lastName || '');
  const [jobTitleModel, setJobTitleModel] = useState<string>(jobTitle || '');
  const [companyModel, setCompanyModel] = useState<AutocompleteEntry[]>(company ? [company] : []);

  const [locationModel, setLocationModel] = useState<{ id: string; text: string }[]>(
    location ? [{ id: location.place_key, text: location.place_name }] : []
  );
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const [validationErrors, setValidationsErrors] = useState<ValidationErrors>({
    required: {
      firstName: false,
      lastName: false,
      jobTitle: false,
      location: false,
      company: false,
    },
    specialCharacter: {
      firstName: false,
      lastName: false,
    },
    maxChar: {
      question: false,
    },
    tooManyDigits: {
      firstName: false,
      lastName: false,
    },
  });

  useEffect(() => {
    const { required, specialCharacter, maxChar, tooManyDigits } = validationErrors;
    setIsFormValid(
      !Object.values(required).some((error) => error) &&
        !Object.values(specialCharacter).some((error) => error) &&
        !Object.values(maxChar).some((error) => error) &&
        !Object.values(tooManyDigits).some((error) => error)
    );
  }, [validationErrors]);

  useEffect(() => {
    setValidationsErrors({
      required: {
        firstName: firstNameModel.trim().length === 0,
        lastName: lastNameModel.trim().length === 0,
        jobTitle: jobTitleModel.trim().length === 0,
        location: locationModel.length === 0,
        company: companyModel.length === 0,
      },
      specialCharacter: {
        firstName: specialCharactersRegex.test(firstNameModel),
        lastName: specialCharactersRegex.test(lastNameModel),
      },
      maxChar: {
        question: questionForSpeaker.trim().length > QUESTION_MAX_SIZE,
      },
      tooManyDigits: {
        firstName: (firstNameModel.match(/\d/g) || []).length > 2,
        lastName: (lastNameModel.match(/\d/g) || []).length > 2,
      },
    });

    setIsFormTouched(
      firstNameModel !== firstName ||
        lastNameModel !== lastName ||
        jobTitleModel !== jobTitle ||
        companyModel[0]?.text !== company?.text ||
        locationModel[0]?.id !== location?.place_key
    );
  }, [
    firstNameModel,
    lastNameModel,
    jobTitleModel,
    locationModel,
    questionForSpeaker,
    companyModel,
  ]);

  if (!isSignedIn || !event) return null;

  const proceedToAttendanceConfirmation = (): void => {
    setShowConfirmInfo(false);
    setIsLoading(false);
    triggerRegistration();
  };

  const onFormSubmission = (): Promise<null | void> => {
    setHasFormBeenValidated(true);
    if (!isFormValid) return Promise.resolve(null);
    setIsLoading(true);

    if (isFormTouched) {
      const body = pickBy({
        first_name: firstNameModel.trim(),
        last_name: lastNameModel.trim(),
        headline: jobTitleModel.trim(),
        place_key: locationModel[0].id,
        user_declared_company_name: companyModel[0]?.customOption && companyModel[0].text,
        user_declared_company_id: !companyModel[0]?.customOption && companyModel[0]?.id,
      });

      // Before saving the first_name, last_name and headline, trim those strings.
      // There is no good use case for having leading or trailing spaces in those fields.
      return updateUserData(body, id)
        .then(proceedToAttendanceConfirmation)
        .catch((error) => {
          // If there is an error when it comes to updating the user details, for example, a
          // backend validation error, then we should not proceed to the attendance confirmation.
          // Instead, we should show an error message and let the user fix the issue.
          setAlertData({ type: 'error', message: error?.message || GENERIC_ERROR_MESSAGE });
          setIsLoading(false);
        });
    }

    return Promise.resolve(proceedToAttendanceConfirmation());
  };

  return (
    <Modal
      show={showConfirmInfo}
      onHide={() => setShowConfirmInfo(false)}
      backdrop='static'
      keyboard={false}
      animation={false}
    >
      <Modal.Header closeButton className='ps-45 pe-4'>
        <h2 className='modal-title fs-3' data-testid='confirm-info-title'>
          Your details
        </h2>
      </Modal.Header>
      <Modal.Body className='px-45 pt-4 pb-35'>
        <form noValidate role='form' aria-label='Confirm your information'>
          <fieldset disabled={isLoading} className='border-0 p-0'>
            <div className='pb-3 pt-25 row'>
              <p>Confirm your profile details to complete the registration.</p>
              <div className='col-6'>
                <div className='pt-25'>
                  <label className='mb-1 form-label' htmlFor='first-name'>
                    First Name<span className='text-orange-devex'> *</span>
                  </label>
                  <div className='input-group has-validation'>
                    <input
                      required
                      id='first-name'
                      className={classNames('form-control rounded-1', {
                        'is-invalid':
                          hasFormBeenValidated &&
                          (validationErrors.required.firstName ||
                            validationErrors.specialCharacter.firstName ||
                            validationErrors.tooManyDigits.firstName),
                      })}
                      tabIndex={1}
                      type='text'
                      name='first-name'
                      aria-label='first-name'
                      value={firstNameModel}
                      onChange={({ target }) => setFirstNameModel(target.value)}
                    />
                    <div id='first-name' className='mt-15 invalid-feedback'>
                      {validationErrors.required.firstName
                        ? 'First name is mandatory.'
                        : validationErrors.tooManyDigits.firstName
                          ? 'Please enter a valid first name containing no more than 2 digits.'
                          : 'Invalid character. Please enter a valid first name.'}
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-6'>
                <div className='pt-25'>
                  <label className='mb-1 form-label' htmlFor='last-name'>
                    Last Name<span className='text-orange-devex'> *</span>
                  </label>
                  <div className='input-group has-validation'>
                    <input
                      required
                      id='last-name'
                      className={classNames('form-control rounded-1', {
                        'is-invalid':
                          hasFormBeenValidated &&
                          (validationErrors.required.lastName ||
                            validationErrors.specialCharacter.lastName ||
                            validationErrors.tooManyDigits.lastName),
                      })}
                      tabIndex={2}
                      type='text'
                      name='last-name'
                      aria-label='last-name'
                      value={lastNameModel}
                      onChange={({ target }) => setLastNameModel(target.value)}
                    />
                    <div id='last-name' className='mt-15 invalid-feedback'>
                      {validationErrors.required.lastName
                        ? 'Last name is mandatory.'
                        : validationErrors.tooManyDigits.lastName
                          ? 'Please enter a valid last name containing no more than 2 digits.'
                          : 'Invalid character. Please enter a valid last name.'}
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12'>
                <div className='pt-35'>
                  <label className='mb-1 form-label' htmlFor='organization'>
                    Organization<span className='text-orange-devex'> *</span>
                  </label>
                  <div className='input-group has-validation'>
                    <AutocompleteBootstrap5
                      onInputChange={(text) => (companyInputValue.current = text)}
                      onBlur={() => {
                        if (!companyModel.length && companyInputValue.current.trim().length > 0) {
                          setCompanyModel([
                            {
                              id: 'new-id-1',
                              text: companyInputValue.current.trim(),
                              customOption: true,
                            },
                          ]);
                        }
                      }}
                      id='organization'
                      minLength={2}
                      inputProps={{
                        className: classNames('form-control rounded-1', {
                          'is-invalid': hasFormBeenValidated && validationErrors.required.company,
                        }),
                        tabIndex: 3,
                        'aria-label': 'organization',
                      }}
                      newSelectionPrefix=''
                      onSelect={(selected) => setCompanyModel(selected ? [selected] : [])}
                      placeholder=''
                      // Only allow for a new company entry when there are no more
                      // suggestions. If, for example, a user types "google" and one of the
                      // suggestions is "Google", then they should not be able to add a new
                      // company. They should rather select the suggestion.
                      allowNew={(suggestions) => !suggestions.length}
                      highlightOnlyResult={false}
                      apiPath={autocompleteCompaniesPath}
                      selected={companyModel}
                      filter={{ s: ['processed', 'viewonly'] }}
                      clearButton={true}
                    />
                  </div>
                  {hasFormBeenValidated && validationErrors.required.company && (
                    <div className='mt-15 fs-7 text-red'>
                      Organization is mandatory.
                    </div>
                  )}
                </div>
              </div>
              <div className='col-12'>
                <div className='pt-35'>
                  <label className='mb-1 form-label' htmlFor='job-title'>
                    Job Title<span className='text-orange-devex'> *</span>
                  </label>
                  <div className='input-group has-validation'>
                    <input
                      required
                      id='job-title'
                      className={classNames('form-control rounded-1', {
                        'is-invalid': hasFormBeenValidated && validationErrors.required.jobTitle,
                      })}
                      tabIndex={3}
                      type='text'
                      name='job-title'
                      aria-label='job-title'
                      value={jobTitleModel}
                      onChange={({ target }) => setJobTitleModel(target.value)}
                    />
                    <div id='job-title' className='mt-15 invalid-feedback'>
                      Job title is mandatory.
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12'>
                <div className='pt-35'>
                  <label className='mb-1 form-label' htmlFor='location'>
                    Location<span className='text-orange-devex'> *</span>
                  </label>
                  <div className='input-group has-validation'>
                    <AutocompleteBootstrap5
                      id='place_key'
                      minLength={1}
                      inputProps={{
                        className: classNames('form-control rounded-1', {
                          'is-invalid': hasFormBeenValidated && validationErrors.required.location,
                        }),
                        tabIndex: 4,
                        'aria-label': 'place_key',
                      }}
                      onSelect={(selected) => setLocationModel(selected ? [selected] : [])}
                      placeholder=''
                      apiPath={autocompletePlacesPath}
                      selected={locationModel}
                      filter={{ types: ['Country', 'City'] }}
                      clearButton={true}
                    />
                  </div>
                  {hasFormBeenValidated && validationErrors.required.location && (
                    <div className='mt-15 fs-7 text-red'>
                      Location is mandatory.
                    </div>
                  )}
                </div>
              </div>
              <div className='col-12'>
                <div className='pt-35'>
                  <label className='form-label' htmlFor='question'>
                    Do you have a question for the speaker(s)?
                  </label>
                  <div className='input-group has-validation'>
                    <textarea
                      rows={3}
                      id='question'
                      className={classNames('form-control rounded-1', {
                        'is-invalid': hasFormBeenValidated && validationErrors.maxChar.question,
                      })}
                      tabIndex={5}
                      name='question'
                      aria-label='question-for-speaker'
                      value={questionForSpeaker}
                      onChange={({ target }) => setQuestionForSpeaker(target.value)}
                    />
                    <div id='question' className='mt-15 invalid-feedback'>
                      Please make sure that your question doesn&apos;t exceed 500 characters.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </form>
      </Modal.Body>
      <Modal.Footer className='px-45'>
        <button
          tabIndex={6}
          disabled={isLoading}
          className='btn btn-link me-3'
          onClick={() => {
            setShowConfirmInfo(false);
          }}
        >
          Cancel
        </button>
        <button
          disabled={isLoading}
          tabIndex={7}
          className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
          onClick={onFormSubmission}
        >
          Register now
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmInfo;
